import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileDown, Loader2, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, eur, formatApiError } from "@/lib/api";
import { downloadFile } from "@/lib/utils2";

function ContractRow({ a }) {
  const [busy, setBusy] = useState(false);
  const pdf = async () => {
    setBusy(true);
    try {
      await downloadFile(`/appointments/${a.id}/pdf`, {}, `contratto_${a.client_name.replace(/\W+/g, "_")}_${a.date}.pdf`);
    } catch {
      toast.error("Errore nella generazione del PDF");
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-3 py-4" data-testid={`contract-row-${a.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{a.client_name}</p>
        <p className="text-xs text-[var(--muted)]">
          Firmato il {new Date(a.signature.signed_at).toLocaleDateString("it-IT")} · appuntamento {new Date(a.date + "T00:00").toLocaleDateString("it-IT")} · {a.items.length} oggetti
        </p>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className="font-mono font-semibold">{eur(a.totals.items_total)}</p>
          <p className="text-xs text-emerald-700">cliente {eur(a.totals.client_share)}</p>
        </div>
        <Link to={`/appuntamenti/${a.id}`} className="btn-secondary h-10 px-4" data-testid={`contract-open-${a.id}`}>Apri</Link>
        <button onClick={pdf} disabled={busy} className="btn-primary h-10 px-4" data-testid={`contract-pdf-${a.id}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
        </button>
      </div>
    </li>
  );
}

export default function Archive() {
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [list, setList] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/contracts", { params: { q, start, end } });
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [q, start, end]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="eyebrow">Solo amministratore</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Archivio contratti</h1>
      </div>
      <div className="rounded-2xl bg-white border border-[var(--border)] p-4 grid sm:grid-cols-[1fr_170px_170px] gap-3" data-testid="archive-filters">
        <div className="space-y-1.5">
          <Label>Cliente</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--muted)]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome o cognome" className="pl-9" data-testid="archive-search-input" />
          </div>
        </div>
        <div className="space-y-1.5"><Label>Firmati dal</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="archive-start-input" /></div>
        <div className="space-y-1.5"><Label>al</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="archive-end-input" /></div>
      </div>
      {list === null ? (
        <p className="text-[var(--muted)]" data-testid="archive-loading">Caricamento...</p>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-12 text-center text-[var(--muted)]" data-testid="archive-empty">
          <FileSignature className="h-8 w-8 mx-auto mb-3" /> Nessun contratto firmato trovato.
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-[var(--border)] px-5">
          <p className="pt-4 text-sm text-[var(--muted)]" data-testid="archive-count">{list.length} contratti</p>
          <ul className="divide-y divide-[var(--border)]" data-testid="archive-list">{list.map((a) => <ContractRow key={a.id} a={a} />)}</ul>
        </div>
      )}
    </div>
  );
}
